import api from '../../services/api';

/**
 * Get filing queue (applications pending filing)
 */
export async function getFilingQueue(options = {}) {
  const params = new URLSearchParams();
  if (options.status) params.append('status', options.status);
  if (options.jurisdiction) params.append('jurisdiction', options.jurisdiction);
  if (options.assignmentFilter) params.append('assignmentFilter', options.assignmentFilter);

  const queryString = params.toString();
  const url = queryString ? `/attorney/filing-queue?${queryString}` : '/attorney/filing-queue';

  const response = await api.get(url);
  return response.data;
}

/**
 * Get a single application for filing
 */
export async function getFilingApplication(applicationId) {
  const response = await api.get(`/attorney/filing-queue/${applicationId}`);
  return response.data;
}

/**
 * Mark a jurisdiction as filed
 * Route: POST /api/attorney/filing/:applicationId/:jurisdiction/mark-filed
 */
export async function markAsFiled(applicationId, data) {
  const response = await api.post(
    `/attorney/filing/${applicationId}/${data.jurisdiction}/mark-filed`,
    {
      ipOfficeRef: data.ipOfficeRef,
      filedAt: data.filedAt || new Date().toISOString(),
      notes: data.notes
    }
  );
  return response.data;
}

/**
 * Update filing status
 */
export async function updateFilingStatus(applicationId, jurisdiction, status, data = {}) {
  const response = await api.patch(`/attorney/filing-queue/${applicationId}/filing`, {
    jurisdiction,
    status,
    ...data
  });
  return response.data;
}

/**
 * Update IP Office reference for a filed jurisdiction
 * Reuses mark-filed endpoint which handles the update
 */
export async function updateIpOfficeRef(applicationId, jurisdiction, ipOfficeRef) {
  const response = await api.post(
    `/attorney/filing/${applicationId}/${jurisdiction}/mark-filed`,
    { ipOfficeRef }
  );
  return response.data;
}

/**
 * Assign an application to self or another attorney
 */
export async function assignApplication(applicationId, attorneyId = null) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/assign`,
    { attorneyId }
  );
  return response.data;
}

/**
 * Transfer application to another organization (attorneyadmin only)
 */
export async function transferApplication(applicationId, targetOrgId, reason = null) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/transfer`,
    { targetOrgId, reason }
  );
  return response.data;
}

/**
 * Unassign attorney from application
 */
export async function unassignApplication(applicationId) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/unassign`
  );
  return response.data;
}

/**
 * Recall application back to MarkoTM (removes org transfer AND attorney assignment)
 */
export async function recallApplication(applicationId) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/recall`
  );
  return response.data;
}

/**
 * Get org members for assignment (admins only)
 */
export async function getOrgMembers() {
  const response = await api.get('/attorney/org-members');
  return response.data;
}

/**
 * Download application summary PDF
 */
export async function downloadFilingPdf(applicationId) {
  const response = await api.get(
    `/attorney/filing/${applicationId}/summary-pdf`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'application/pdf' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Application-Summary-${applicationId}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Save amendments to an application (admin/attorneyadmin only)
 */
export async function amendApplication(applicationId, data) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/amend`,
    data
  );
  return response.data;
}

/**
 * Send amendment notification email to customer (admin/attorneyadmin only)
 */
export async function sendAmendmentEmail(applicationId, data) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/send-amendment-email`,
    data
  );
  return response.data;
}

/**
 * Get amendment history for an application
 */
export async function getAmendments(applicationId) {
  const response = await api.get(
    `/attorney/applications/${applicationId}/amendments`
  );
  return response.data;
}

/**
 * Manually approve pending amendments (e.g. customer called to confirm)
 * @param {string} applicationId
 * @param {string} [amendmentId] - Optional: approve a specific entry. Omit to approve all.
 */
export async function approveAmendment(applicationId, amendmentId) {
  const response = await api.post(
    `/attorney/applications/${applicationId}/amend/approve`,
    amendmentId ? { amendmentId } : {}
  );
  return response.data;
}

export default {
  getFilingQueue,
  getFilingApplication,
  markAsFiled,
  updateFilingStatus,
  updateIpOfficeRef,
  assignApplication,
  transferApplication,
  unassignApplication,
  recallApplication,
  getOrgMembers,
  downloadFilingPdf,
  amendApplication,
  sendAmendmentEmail,
  getAmendments,
  approveAmendment
};
